import { database } from './config';
import { ref as dbRef, push, set } from 'firebase/database';
import { PDFDocument } from 'pdf-lib';

// Function to extract selected pages from PDF
const extractSelectedPages = async (file, selectedPages) => {
  try {
    if (file.type !== 'application/pdf') {
      // For non-PDF files, return original file
      console.log('📄 Non-PDF file detected, returning original file');
      return file;
    }

    console.log('🔧 STARTING PAGE EXTRACTION PROCESS');
    console.log('📄 Original PDF:', file.name, 'Size:', file.size);
    console.log('📋 Pages to extract:', selectedPages);
    
    const arrayBuffer = await file.arrayBuffer();
    const originalPdf = await PDFDocument.load(arrayBuffer);
    const totalPagesInOriginal = originalPdf.getPageCount();
    
    console.log('📖 Original PDF has', totalPagesInOriginal, 'total pages');
    console.log('✂️ Extracting pages:', selectedPages.join(', '));
    
    const newPdf = await PDFDocument.create();
    
    // Copy selected pages to new PDF
    for (let i = 0; i < selectedPages.length; i++) {
      const pageNumber = selectedPages[i];
      const pageIndex = pageNumber - 1; // Convert to 0-based index
      
      console.log(`📃 Processing page ${i + 1}/${selectedPages.length}: Page ${pageNumber} (index ${pageIndex})`);
      
      if (pageIndex >= 0 && pageIndex < totalPagesInOriginal) {
        const [copiedPage] = await newPdf.copyPages(originalPdf, [pageIndex]);
        newPdf.addPage(copiedPage);
        console.log(`✅ Successfully added page ${pageNumber} to new PDF`);
      } else {
        console.log(`⚠️ Page ${pageNumber} is out of range (PDF has ${totalPagesInOriginal} pages)`);
      }
    }
    
    const finalPageCount = newPdf.getPageCount();
    console.log('📊 New PDF created with', finalPageCount, 'pages');
    
    // Generate new PDF bytes
    const pdfBytes = await newPdf.save();
    
    // Create new file blob with selected pages
    const newFile = new File([pdfBytes], `${file.name.replace('.pdf', '')}_pages_${selectedPages.join('-')}.pdf`, {
      type: 'application/pdf'
    });
    
    console.log('✅ PAGE EXTRACTION COMPLETED');
    console.log('📄 New file name:', newFile.name);
    console.log('📏 New file size:', newFile.size);
    console.log('🎯 Expected pages in final PDF: ', selectedPages.length);
    console.log('✅ Actual pages in final PDF:', finalPageCount);
    
    if (finalPageCount !== selectedPages.length) {
      console.error('🚨 PAGE COUNT MISMATCH! Expected:', selectedPages.length, 'Actual:', finalPageCount);
    }
    
    // Add download option for extracted PDF
    const downloadUrl = URL.createObjectURL(newFile);
    console.log('📥 Download extracted PDF from:', downloadUrl);
    console.log('📥 To download, run: downloadExtractedPDF("' + downloadUrl + '", "' + newFile.name + '")');
    
    // Add global download function
    if (typeof window !== 'undefined') {
      window.downloadExtractedPDF = (url, filename) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      };
    }
    
    return newFile;
    
  } catch (error) {
    console.error('Error extracting pages:', error);
    // If extraction fails, return original file
    return file;
  }
};

export const uploadToDatabase = async (file, customerInfo = {}, selectedPages = [], totalCost = 0, targetPrinter = 'hp_1', copies = 1) => {
  let processedFile = file; // Initialize with original file
  
  try {
    console.log('🔥 Firebase Upload Started:', {
      fileName: file.name,
      fileSize: file.size,
      selectedPages: selectedPages,
      totalCost: totalCost,
      targetPrinter: targetPrinter,
      timestamp: new Date().toISOString()
    });
    
    console.log('Starting upload process for:', file.name);
    console.log('Selected pages:', selectedPages);
    console.log('Is PDF file:', file.type === 'application/pdf');
    console.log('Should extract pages:', selectedPages.length > 0 && file.type === 'application/pdf');
    
    // Extract selected pages if it's a PDF
    // Process file to extract selected pages if needed
    if (selectedPages.length > 0 && file.type === 'application/pdf') {
      console.log('🔧 Extracting selected pages from PDF...');
      processedFile = await extractSelectedPages(file, selectedPages);
      console.log('✅ Page extraction completed:', {
        originalSize: file.size,
        newSize: processedFile.size,
        originalName: file.name,
        newName: processedFile.name
      });
    } else {
      console.log('ℹ️ No page extraction needed - using original file');
    }
    
    if (processedFile.size > 5 * 1024 * 1024) {
      return {
        success: false,
        error: 'File size too large. Please select a file smaller than 5MB.',
        details: `File size: ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`
      };
    }
    
    // Read file as data URL first
    const dataURL = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(processedFile);
    });
    
    // Extract pure base64 string (remove 'data:application/pdf;base64,' prefix)
    // This matches the format expected by printer_listener.py
    const base64Data = dataURL.split(',')[1];
    
    const printJob = {
      // Core fields required by printer_listener.py
      fileName: processedFile.name,
      base64: base64Data,  // Changed from 'fileData' to 'base64' to match printer_listener.py
      status: 'payment_pending', // Will be changed to 'pending' by backend after successful payment
      targetPrinter: targetPrinter,
      copies: copies,
      
      // Additional metadata for tracking
      originalFileName: file.name,
      fileSize: processedFile.size,
      originalFileSize: file.size,
      fileType: processedFile.type,
      uploadMethod: 'database-direct-selective',
      createdAt: new Date().toISOString(), // Using ISO string instead of serverTimestamp
      customerInfo: {
        name: customerInfo.name || 'Customer',
        email: customerInfo.email || '',
        phone: customerInfo.phone || ''
      },
      printSettings: {
        selectedPages: selectedPages,
        totalPages: selectedPages.length,
        totalCost: totalCost,
        costPerPage: 2,
        isSelectivePages: selectedPages.length > 0 && file.type === 'application/pdf'
      },
      printerSettings: {
        copies: copies,
        paperSize: 'A4',
        orientation: 'portrait',
        color: 'auto'
      },
      paymentStatus: 'completed',
      jobId: `job_${Date.now()}`
    };
    
    console.log('📊 DETAILED Print job data prepared:');
    console.log('- File Name:', printJob.fileName);
    console.log('- Original File Name:', printJob.originalFileName);
    console.log('- File Size (processed):', printJob.fileSize);
    console.log('- Original File Size:', printJob.originalFileSize);
    console.log('- Selected Pages:', printJob.printSettings.selectedPages);
    console.log('- Total Pages to Print:', printJob.printSettings.totalPages);
    console.log('- Is Selective Pages:', printJob.printSettings.isSelectivePages);
    console.log('- Job ID:', printJob.jobId);
    console.log('- Base64 Data Length:', base64Data.length);
    console.log('- Status:', printJob.status);
    console.log('🖨️ Format matches printer_listener.py expectations:');
    console.log('  ✓ fileName:', printJob.fileName);
    console.log('  ✓ base64: <pure base64 string without data URL prefix>');
    console.log('  ✓ status:', printJob.status);
    
    // CRITICAL: Verify that the processed file contains only selected pages
    if (printJob.printSettings.isSelectivePages) {
      console.log('🚨 SELECTIVE PAGES MODE DETECTED');
      console.log('🚨 Python service should receive file with ONLY', selectedPages.length, 'pages');
      console.log('🚨 Pages to print:', selectedPages.join(', '));
      console.log('🚨 If more pages are printed, the issue is in the PDF processing');
    }
    
    const printJobsRef = dbRef(database, 'printJobs');
    console.log('🔗 Database reference created, attempting push...');
    console.log('📍 Database URL:', database.app.options.databaseURL);
    console.log('📍 Reference path: printJobs');
    
    let jobRef;
    let attempts = 0;
    const maxAttempts = 3;
    
    while (attempts < maxAttempts) {
      try {
        attempts++;
        console.log(`🔄 Database push attempt ${attempts}/${maxAttempts}...`);
        console.log('📦 Pushing data:', JSON.stringify({
          fileName: printJob.fileName,
          status: printJob.status,
          base64Length: printJob.base64.length,
          createdAt: printJob.createdAt
        }));
        
        // Use set with push to ensure data is written
        jobRef = push(printJobsRef);
        await set(jobRef, printJob);
        
        console.log('✅ Database push successful! Job key:', jobRef.key);
        console.log('🔍 Verify at: https://console.firebase.google.com/project/printer-setup-7c805/database');
        break; // Success, exit loop
        
      } catch (pushError) {
        console.error(`❌ Database push attempt ${attempts} failed:`, pushError);
        
        if (attempts >= maxAttempts) {
          throw new Error(`Database push failed after ${maxAttempts} attempts: ${pushError.message}`);
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
      }
    }
    
    return {
      success: true,
      jobId: jobRef.key,
      fileName: processedFile.name,
      originalFileName: file.name,
      uploadMethod: 'database-direct-selective',
      message: `File uploaded successfully! ${selectedPages.length > 0 && file.type === 'application/pdf' ? `Selected pages (${selectedPages.join(', ')}) extracted and ` : ''}Print job created.`,
      fileSize: processedFile.size,
      originalFileSize: file.size,
      fileType: processedFile.type,
      selectedPages: selectedPages,
      isSelectivePages: selectedPages.length > 0 && file.type === 'application/pdf'
    };
    
  } catch (error) {
    console.error('❌ Firebase upload completely failed:', error);
    
    // Fallback: Store locally and show user the data
    const fallbackData = {
      jobId: `local_${Date.now()}`,
      fileName: processedFile ? processedFile.name : file.name,
      originalFileName: file.name,
      selectedPages,
      totalCost,
      timestamp: new Date().toISOString(),
      status: 'stored_locally',
      error: error.message
    };
    
    try {
      localStorage.setItem(`printJob_${fallbackData.jobId}`, JSON.stringify(fallbackData));
      console.log('💾 Data stored locally as fallback');
    } catch (localError) {
      console.error('❌ Local storage also failed:', localError);
    }
    
    return {
      success: false,
      error: 'Firebase connection failed. Please check your internet connection and try again.',
      details: error.message,
      fallbackData: fallbackData,
      uploadMethod: 'failed-local-fallback'
    };
  }
};
